import React, { FC, ReactNode } from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';

type PriceRequestSubmitCardProps = {
    title: string;
    /** success: send / respond (green); error: cancel or discard (red, destructive). */
    tone?: 'success' | 'error';
    children: ReactNode;
    /** The send button; omitted for the "already sent" state, which only explains. */
    submit?: {
        label: string;
        onClick: () => void;
        disabled?: boolean;
        dataTest: string;
    };
};

/**
 * DD-3697: a deliberate step on a request — sending (green) or cancelling (red) — kept apart
 * from Save so neither is clicked by accident: a card with what happens next and one large button.
 */
const PriceRequestSubmitCard: FC<PriceRequestSubmitCardProps> = ({ title, tone = 'success', children, submit }) => (
    <Paper
        variant="outlined"
        sx={{ p: 2.5 }}
        data-test={`price-request.${tone === 'error' ? 'cancel' : 'submit'}-card`}
    >
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <Box display="flex" gap={1.5} flex="1 1 320px">
                {tone === 'error' ? (
                    <CancelOutlinedIcon color="error" sx={{ mt: 0.25 }} />
                ) : submit ? (
                    <SendOutlinedIcon color="success" sx={{ mt: 0.25 }} />
                ) : (
                    <CheckCircleOutlineIcon color="success" sx={{ mt: 0.25 }} />
                )}
                <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                        {title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" component="div">
                        {children}
                    </Typography>
                </Box>
            </Box>
            {submit ? (
                <Box flexShrink={0}>
                    <Button
                        variant="contained"
                        color={tone}
                        size="large"
                        disabled={submit.disabled}
                        onClick={submit.onClick}
                        data-test={submit.dataTest}
                    >
                        {submit.label}
                    </Button>
                </Box>
            ) : undefined}
        </Box>
    </Paper>
);

export default PriceRequestSubmitCard;
